<template>
  <Breadcrumbs :breadcrumbs="breadcrumbs" />

  <!-- Offers -->
  <OffersGrid
    :pagination="{ limit: 24, offset: 0 }"
    :input="{ brandSlug: brandSlug }"
    :title="$t('brand.brand-offers', [brandName])"
    :tag="'h1'"
    :infinite-loading="true"
    class="mb-2"
  >
    <template #content>
      <DynamicContent id="brand-content-introduction" :html="content?.introductionBlock" :with-container="false">
      </DynamicContent>
    </template>
  </OffersGrid>

  <!-- Ad -->
  <Advertisement :display-at="AdDisplayAt.Brand" class="ad mb-1" />

  <DynamicContent
    id="brand-content-similar"
    class="container dynamic-content"
    :html="content?.similarBlock"
  ></DynamicContent>

  <!-- This brand's folders -->
  <BrochuresGrid
    :input="{ brandSlug: brandSlug }"
    :pagination="{ limit: 3, offset: 0 }"
    :title="isContentBlockEmpty(content?.similarBlock) ? $t('brand.brand-brochures', [brandName]) : undefined"
    :tag="'h2'"
    :impression-source="BrochureImpressionSource.Brand"
    :infinite-loading="false"
    :show-load-more="true"
    class="mb-2"
  />

  <CollapsibleSeoText
    :logo="data?.brand?.fileUrl || undefined"
    :title="brandName"
    :description="data?.brand?.description || ''"
    class="mb-4"
    :tag="'h2'"
  />

  <DynamicContent id="brand-content-timed" class="container dynamic-content" :html="content?.timedBlock">
  </DynamicContent>
  <DynamicContent id="brand-content-offers" class="container dynamic-content" :html="content?.offersBlock">
  </DynamicContent>
</template>

<script setup lang="ts">
import {
  AdDisplayAt,
  BrochureImpressionSource,
  useBrandQuery,
  useWebsitePageQuery,
} from "~/graphql/generated";
// This is the /brand/offers route, where we show the offers for a specific brand

// It is a bit unfortunate that it shares a slug with the catch-all route, but that's just the nuxt filesystem routing
false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    "nl-NL": "/merken/[slug]",
    fr: "/marques/[slug]",
    nl: "/merken/[slug]",
  },
});

const brandSlug = useRoute().params.slug as string;

// Fetch the brand by slug
const { data, error, fetching } = await useBrandQuery({
  variables: {
    brandInput: {
      slug: brandSlug,
    },
  },
});
if (error?.value || !data.value?.brand) {
  throw createError({ statusCode: 404, fatal: true });
}

const brandName = computed(() => {
  return data.value?.brand?.name ?? "";
});

// Set the breadcrumbs
const breadcrumbs = [
  {
    to: "brands",
  },
  {
    to: "brands-slug",
    text: brandName.value,
  },
];

const { t } = useI18n();
const translateWithHost = useTranslateWithHost();

const brandTitle = computed(() => t("brand.title", [brandName.value]));
/** Host aware meta description. */
const brandDescription = computed(() =>
  t("brand.meta-description", {
    brand: brandName.value,
    siteName: translateWithHost("title"),
  }),
);

useHead({
  title: brandTitle,
  meta: [{ name: "description", content: brandDescription }],
});

useSeoMeta({
  ogTitle: brandTitle,
  ogDescription: brandDescription,
  ogImage: computed(() => data.value?.brand?.fileUrl || undefined),
});

// Set the (optional) page content blocks
const path = useRoute().path;
const { websitePagePath } = useWebsitePagePath(path);
const page = await useWebsitePageQuery({ variables: websitePagePath });
const content = useDynamicContent(page.data);
</script>
